import * as Sentry from '@sentry/react';
import { bootstrapAuthApp, getUser, normalizeKeycloakAuthority } from 'auth';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import App from '@/App.tsx';
import 'ui/tailwind.css';
import '@/index.css';

if (import.meta.env.VITE_NODE_ENV !== 'dev') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracePropagationTargets: [import.meta.env.VITE_API_URL],
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend: (event, hint) => {
      const user = getUser();

      if (user) {
        event.extra = { ...(event.extra || {}), userEmail: user.profile.email };
      }

      // hint.originalException may contain an original exception,
      // For example, an Axios error with the Response.status field
      const error = hint.originalException as any;
      console.log('Sentry error: ', error);
      if (error && error?.response && error?.response?.status !== 500) {
        event.tags = { ...(event.tags || {}), frontendError: true };
        // If the status of the answer is not 500, we return NULL - the event will not be sent to Sentry
        // return null;
      }
      return event;
    },
  });
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
const keycloakAuthority = normalizeKeycloakAuthority(import.meta.env.VITE_KEYCLOAK_AUTHORITY);

const renderApp = (userManager: UserManager) => {
  return import.meta.env.VITE_NODE_ENV !== 'dev' ? (
    <React.StrictMode>
      <AuthProvider userManager={userManager}>
        <App />
      </AuthProvider>
    </React.StrictMode>
  ) : (
    <AuthProvider userManager={userManager}>
      <App />
    </AuthProvider>
  );
};

void bootstrapAuthApp({
  root,
  authority: keycloakAuthority,
  selectorUrl: import.meta.env.VITE_APP_SELECTOR_URL,
  createUserManager: () =>
    new UserManager({
      authority: keycloakAuthority,
      client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
      redirect_uri: import.meta.env.VITE_KEYCLOAK_REDIRECT_URI,
      response_type: 'code',
      scope: 'openid profile email organization roles',
      loadUserInfo: true,
      automaticSilentRenew: true,
      post_logout_redirect_uri: import.meta.env.VITE_APP_SELECTOR_URL || window.location.origin,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    }),
  renderApp,
});
